import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const phases = [
  {
    n: "I.",
    label: "Discovery",
    duration: "Weeks 1 – 2",
    title: "Fit conversation with chair and nominating committee",
    body:
      "A structured conversation on mandate, industry exposure, committee expectations and board dynamics — before any formal document exchange.",
  },
  {
    n: "II.",
    label: "Due Diligence",
    duration: "Weeks 3 – 4",
    title: "Two-way due diligence on strategy and books",
    body:
      "Independent review of the last three annual reports, capital work-in-progress schedules, litigation register and prior board minutes — with a written point-of-view returned to the chair.",
  },
  {
    n: "III.",
    label: "Onboarding",
    duration: "Month 2",
    title: "IICA-guided induction and committee alignment",
    body:
      "Board handbook, plant visits, first-tier management interviews, and a working cadence with the company secretary and audit / risk committee chairs.",
  },
  {
    n: "IV.",
    label: "Cadence",
    duration: "Ongoing",
    title: "Quarterly rhythm and independent commentary",
    body:
      "Structured pre-read on every board pack, targeted follow-up on CAPEX and risk, and a private independent note to the chair once a quarter — the discipline of continuous oversight, not seasonal review.",
  },
];

const row = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function EngagementModel() {
  return (
    <section id="engagement" className="relative border-b border-surface-2 bg-[#0b1a30]">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">07 —</span><span className="ml-3">Engagement Model</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              How a board <span className="italic text-brass">would work with me</span> —
              from first conversation to standing cadence.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 text-mutedfg text-sm md:text-base font-light leading-relaxed">
            Four phases. Written for chairs and nominating committees who prefer a documented working method
            over an implicit one.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-surface-2 border-y border-surface-2">
          {phases.map((p, i) => (
            <motion.div
              key={p.n}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={row}
              className="bg-ink p-8 md:p-10 flex flex-col min-h-[380px] group placard border border-transparent"
              data-testid={`phase-${i}`}
            >
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-serif-display italic text-brass text-3xl">{p.n}</span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-mutedfg tabular">{p.duration}</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-4">{p.label}</div>
              <h3 className="font-serif-display text-ivory text-xl md:text-2xl font-light leading-[1.15] transition-colors duration-500 group-hover:text-brass">
                {p.title}
              </h3>
              <p className="mt-5 text-mutedfg leading-relaxed font-light text-sm md:text-base flex-1">
                {p.body}
              </p>
              <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-mutedfg">
                <span className="h-px w-8 bg-brass/60" />
                <span>Phase 0{i + 1} / 04</span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 border border-surface-2 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-2">Availability</div>
            <div className="font-serif-display text-ivory text-2xl md:text-3xl font-light leading-tight">
              Currently open to <span className="italic text-brass">1 – 2 independent directorships</span> in
              listed Indian companies.
            </div>
          </div>
          <a
            href="#contact"
            data-testid="engagement-cta"
            className="group inline-flex items-center gap-3 bg-brass px-8 py-4 text-ink text-[13px] uppercase tracking-[0.2em] font-semibold transition-transform duration-500 hover:-translate-y-[2px] shrink-0"
          >
            Initiate Conversation
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
